import { type FC } from "react";
import { Toolbar, Checkbox, Button, Typography, Divider } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";
import { ToolbarProgress, useConfirmAction } from "@/shared/components";
import {
  marketingErrorKey,
  useDeleteEmailTemplatesMutation,
} from "@/partner/entities/marketing";

interface EmailTemplatesListToolbarProps {
  indeterminate: boolean;
  allSelected: boolean;
  numSelected: number;
  onSelectAllClick: (event: React.ChangeEvent<HTMLInputElement>) => void;
  selectedIds: string[];
  onCloseSelected: () => void;
}

/** Тулбар выбранных писем: массовое удаление, как PollsLibraryListToolbar. */
export const EmailTemplatesListToolbar: FC<EmailTemplatesListToolbarProps> = ({
  indeterminate,
  allSelected,
  numSelected,
  onSelectAllClick,
  selectedIds,
  onCloseSelected,
}) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const { confirm, dialog: confirmDialog } = useConfirmAction();
  const [deleteTemplates, { loading }] = useDeleteEmailTemplatesMutation();

  const onDeleteSelected = () => {
    confirm({
      title: t("common.actions.delete"),
      text: t("emailTemplates.list.deleteConfirm"),
      onConfirm: () =>
        deleteTemplates({
          variables: { ids: selectedIds },
          onCompleted: () => {
            onCloseSelected();
            enqueueSnackbar(t("emailTemplates.list.deleted"), {
              variant: "success",
            });
          },
          onError: (error) =>
            enqueueSnackbar(t(marketingErrorKey(error)), { variant: "error" }),
        }),
    });
  };

  return (
    <Toolbar sx={{ position: "relative" }}>
      <Checkbox
        color="default"
        indeterminate={indeterminate}
        checked={allSelected}
        onChange={onSelectAllClick}
        sx={{ marginLeft: "-16px", marginRight: { xs: 0, sm: "16px" } }}
      />
      <Typography>
        {t("common.toolbar.selectedCount", { count: numSelected })}
      </Typography>
      <Divider
        orientation="vertical"
        variant="middle"
        sx={{ mx: 1.25, height: "2em" }}
      />
      <Button
        color="inherit"
        onClick={onDeleteSelected}
        disabled={loading || numSelected === 0}
      >
        {t("common.actions.delete")}
      </Button>
      <ToolbarProgress loading={loading} />
      {confirmDialog}
    </Toolbar>
  );
};
